import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { USE_SEO_BASE } from '../core/http/http-context.tokens';
import { NotificationService } from '../core/services/notification.service';
import { LocalStorageService } from '../core/services/local-storage.service';
import { CoreTargetService } from '../core/services/target.service';
import { FORCE_REGION } from '../core/http/http-context.tokens';
import { ExperimentResponse } from './agentic-ecommerce/agentic-ecommerce.types';

@Injectable({
  providedIn: 'root',
})
export class WebsiteAuditService {
  constructor(
    private http: HttpClient,
    private router: Router,
    private notificationService: NotificationService,
    private localStorageService: LocalStorageService,
    private targetService: CoreTargetService,
  ) {}

  private auditTasks = new BehaviorSubject<any>([]);
  getAuditTasks() {
    return this.auditTasks.asObservable();
  }
  updateAuditTasks(tasks: any) {
    this.auditTasks.next(tasks);
  }
  fetchAuditTasks() {
    function getDomainFromUrl(url: string): string | null {
      try {
        const normalized = url.startsWith('http') ? url : `https://${url}`;

        const { hostname } = new URL(normalized);
        return hostname.replace(/^www\./, '');
      } catch {
        return null;
      }
    }

    // This shared list feeds the audit-view selection AND the sidenav audit
    // selector (whose search filters this list client-side), so pull them all in
    // one page — otherwise the selector shows a partial list and its search
    // can't find audits beyond it. The Audits tab paginates independently.
    this.getAuditTasksList({ page: 1, limit: 5000 }).subscribe({
      next: (res) => {
        const tasks = res?.tasks || [];
        tasks.forEach((element: any) => {
          element['domain'] = getDomainFromUrl(element.url);
        });

        this.updateAuditTasks(tasks);
      },
      error: (err) => {
        this.notificationService.error(err?.error?.message);
      },
    });
  }

  private auditTasksLoading = new BehaviorSubject<any>(false);
  getAuditTasksLoading() {
    return this.auditTasksLoading.asObservable();
  }
  updateAuditTasksLoading(loading: boolean) {
    this.auditTasksLoading.next(loading);
  }

  // True while the sub-bar is re-fetching the audit for a competitor switch, so
  // every tab can show a subtle overlay while its data updates.
  private competitorSwitching = new BehaviorSubject<boolean>(false);
  getCompetitorSwitching() {
    return this.competitorSwitching.asObservable();
  }
  setCompetitorSwitching(value: boolean) {
    this.competitorSwitching.next(value);
  }

  private selectedAuditTask = new BehaviorSubject(null);
  getSelectedAuditTask() {
    return this.selectedAuditTask.asObservable();
  }
  updateSelectedAuditTask(task: any) {
    this.router.navigate([], {
      queryParams: {
        // Keep the URL on the stable parent/canonical id so a page reload can
        // still match the audit in the list (the detail returns the run id).
        taskId: task?.parentTaskId || task?.taskId,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });

    this.selectedAuditTask.next(task);
  }

  getAuditGeneral(data: any): Observable<any> {
    return this.http.post('/keywords/get-audit-general', data, {
      context: new HttpContext().set(USE_SEO_BASE, true),
    });
  }

  // Some endpoints wrap the payload in { success, data }. Unwrap it.
  private unwrap(res: any): any {
    if (res && res.success !== undefined && res.data !== undefined) {
      return res.data;
    }
    return res;
  }

  // The single-task response is nested as
  // { target, selectedCompetitor, availableCompetitors, overview, sections }.
  // Flatten `target` to the top level (so taskId/url/status keep working) and
  // attach the new report blocks for the redesigned tabs to consume.
  private normalizeTask(res: any): any {
    const payload = this.unwrap(res);
    let task: any;
    if (payload && payload.target && typeof payload.target === 'object') {
      task = {
        ...payload.target,
        overview: payload.overview ?? null,
        sections: payload.sections ?? null,
        selectedCompetitor: payload.selectedCompetitor ?? null,
        availableCompetitors: payload.availableCompetitors ?? [],
      };
    } else {
      task = payload;
    }

    // The single-task detail can omit `type` (only the list guarantees it).
    // Without it a Crawler/Agentic Ecommerce audit looks like a full audit
    // downstream — e.g. the Overview tab wrongly stays enabled instead of
    // deferring to the Agentic tab. Backfill it from the cached list, matching
    // on either the run taskId or the canonical parentTaskId.
    if (task && task.type == null) {
      const ids = [task.taskId, task.parentTaskId].filter(Boolean);
      const match = (this.auditTasks.value || []).find((t: any) =>
        [t?.taskId, t?.parentTaskId]
          .filter(Boolean)
          .some((id: any) => ids.includes(id)),
      );
      if (match?.type != null) {
        task.type = match.type;
      } else if (ids.some((id: any) => String(id).startsWith('crawl_'))) {
        // Agentic Ecommerce audits are created crawl-only and always get a
        // `crawl_`-prefixed taskId — a reliable signal when the list isn't
        // loaded (e.g. the public share page has no audit list to match against).
        task.type = 'Crawler';
      }
    }

    return task;
  }

  // Paginated audits list. Returns { tasks, pagination } — see normalizeTasksList
  // for the shapes tolerated. page/limit/search map straight onto the endpoint.
  getAuditTasksList(params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  }): Observable<{ tasks: any[]; pagination: any }> {
    const email: string = this.localStorageService.get('email') || '';
    const isBehamicsUser = email?.endsWith('@behamics.com') ? true : false;
    const target = this.targetService.getSelectedTarget('target');

    let qp = `target=${target}`;
    if (isBehamicsUser) {
      qp += `&behamicsUser=${isBehamicsUser}`;
    }
    qp += `&page=${params?.page ?? 1}&limit=${params?.limit ?? 20}`;
    const search = (params?.search || '').trim();
    if (search) {
      qp += `&search=${encodeURIComponent(search)}`;
    }
    if (params?.sortBy) {
      qp += `&sortBy=${encodeURIComponent(params.sortBy)}`;
      qp += `&sortDirection=${params.sortDirection === 'desc' ? 'desc' : 'asc'}`;
    }

    return this.http
      .get(`/audit-url/tasks?${qp}`, {
        context: new HttpContext().set(FORCE_REGION, 'west-eu'),
      })
      .pipe(map((res) => this.normalizeTasksList(res)));
  }

  // The list endpoint now returns { tasks, pagination }. Stay tolerant of the
  // older bare array and the { success, data } envelope.
  private normalizeTasksList(res: any): { tasks: any[]; pagination: any } {
    const payload = this.unwrap(res);
    if (Array.isArray(payload)) {
      return { tasks: payload, pagination: null };
    }
    if (payload && Array.isArray(payload.tasks)) {
      return { tasks: payload.tasks, pagination: payload.pagination || null };
    }
    return { tasks: [], pagination: null };
  }

  getAuditTask(taskId: string, competitorUrl?: string): Observable<any> {
    const email: string = this.localStorageService.get('email') || '';
    const isBehamicsUser = email?.endsWith('@behamics.com') ? true : false;
    const target = this.targetService.getSelectedTarget('target');

    let params = `target=${target}`;

    if (isBehamicsUser) {
      params += `&behamicsUser=${isBehamicsUser}`;
    }

    if (competitorUrl) {
      params += `&competitorUrl=${encodeURIComponent(competitorUrl)}`;
    }

    return this.http
      .get(`/audit-url/tasks/${taskId}?${params}`, {
        context: new HttpContext().set(FORCE_REGION, 'west-eu'),
      })
      .pipe(map((res) => this.normalizeTask(res)));
  }

  downloadWebsiteAuditReport(
    taskId: string,
    competitorUrl?: string,
  ): Observable<Blob> {
    let params = new HttpParams();
    if (competitorUrl) {
      params = params.set('competitorUrl', competitorUrl);
    }

    return this.http.get(
      `/audit-url/tasks/${encodeURIComponent(taskId)}/report`,
      {
        params,
        responseType: 'blob',
        context: new HttpContext().set(FORCE_REGION, 'west-eu'),
      },
    );
  }

  getCompetitors(taskId: string): Observable<any> {
    const email: string = this.localStorageService.get('email') || '';
    const isBehamicsUser = email?.endsWith('@behamics.com') ? true : false;
    const target = this.targetService.getSelectedTarget('target');

    let params = `target=${target}`;
    if (isBehamicsUser) params += `&behamicsUser=${isBehamicsUser}`;

    return this.http.get(`/audit-url/tasks/${taskId}/competitors?${params}`, {
      context: new HttpContext().set(FORCE_REGION, 'west-eu'),
    });
  }

  addCompetitor(taskId: string, data: any): Observable<any> {
    const email: string = this.localStorageService.get('email') || '';
    const isBehamicsUser = email?.endsWith('@behamics.com') ? true : false;
    const target = this.targetService.getSelectedTarget('target');

    let params = `target=${target}`;
    if (isBehamicsUser) params += `&behamicsUser=${isBehamicsUser}`;

    return this.http.post(`/audit-url/tasks/${taskId}/competitors?${params}`, data, {
      context: new HttpContext().set(FORCE_REGION, 'west-eu'),
    });
  }

  // Toggle a competitor on/off for the (parent) audit. The GET competitors
  // list doesn't return this flag, so callers track it client-side.
  setCompetitorActive(
    taskId: string,
    competitorId: string,
    isActive: boolean,
  ): Observable<any> {
    const email: string = this.localStorageService.get('email') || '';
    const isBehamicsUser = email?.endsWith('@behamics.com') ? true : false;
    const target = this.targetService.getSelectedTarget('target');

    let params = `target=${target}`;
    if (isBehamicsUser) params += `&behamicsUser=${isBehamicsUser}`;

    return this.http.patch(
      `/audit-url/tasks/${taskId}/competitors/${competitorId}/active?${params}`,
      { isActive },
      {
        context: new HttpContext().set(FORCE_REGION, 'west-eu'),
      },
    );
  }

  addAuditTask(task: any): Observable<any> {
    const email: string = this.localStorageService.get('email') || '';
    const isBehamicsUser = email?.endsWith('@behamics.com') ? true : false;
    const target = this.targetService.getSelectedTarget('target');

    let params = `target=${target}`;

    if (isBehamicsUser) {
      params += `&behamicsUser=${isBehamicsUser}`;
    }

    return this.http.post(`/audit-url/tasks?${params}`, task, {
      context: new HttpContext().set(FORCE_REGION, 'west-eu'),
    });
  }

  rerunAudit(data: any): Observable<any> {
    const email: string = this.localStorageService.get('email') || '';
    const isBehamicsUser = email?.endsWith('@behamics.com') ? true : false;
    const target = this.targetService.getSelectedTarget('target');

    let params = `target=${target}`;

    if (isBehamicsUser) {
      params += `&behamicsUser=${isBehamicsUser}`;
    }

    return this.http.post(`/audit-url/tasks/runAudit?${params}`, data, {
      context: new HttpContext().set(FORCE_REGION, 'west-eu'),
    });
  }

  // Convert a one-time audit into a recurring bi-weekly audit.
  // POST /audit-url/tasks/:taskId/biweekly — the taskId in the path is all that's
  // required; the body is optional.
  convertToBiweekly(taskId: string): Observable<any> {
    const email: string = this.localStorageService.get('email') || '';
    const isBehamicsUser = email?.endsWith('@behamics.com') ? true : false;
    const target = this.targetService.getSelectedTarget('target');

    let params = `target=${target}`;

    if (isBehamicsUser) {
      params += `&behamicsUser=${isBehamicsUser}`;
    }

    return this.http.post(
      `/audit-url/tasks/${taskId}/biweekly?${params}`,
      {},
      {
        context: new HttpContext().set(FORCE_REGION, 'west-eu'),
      },
    );
  }

  // Toggle an audit active/inactive.
  // PATCH /audit-url/tasks/:taskId/active { isActive }
  setAuditActive(taskId: string, isActive: boolean): Observable<any> {
    const email: string = this.localStorageService.get('email') || '';
    const isBehamicsUser = email?.endsWith('@behamics.com') ? true : false;
    const target = this.targetService.getSelectedTarget('target');

    let params = `target=${target}`;

    if (isBehamicsUser) {
      params += `&behamicsUser=${isBehamicsUser}`;
    }

    return this.http.patch(
      `/audit-url/tasks/${taskId}/active?${params}`,
      { isActive },
      {
        context: new HttpContext().set(FORCE_REGION, 'west-eu'),
      },
    );
  }

  deleteAudit(taskId: string): Observable<any> {
    const email: string = this.localStorageService.get('email') || '';
    const isBehamicsUser = email?.endsWith('@behamics.com') ? true : false;
    const target = this.targetService.getSelectedTarget('target');

    let params = `target=${target}`;

    if (isBehamicsUser) {
      params += `&behamicsUser=${isBehamicsUser}`;
    }

    return this.http.delete(`/audit-url/tasks?taskId=${taskId}&${params}`, {
      context: new HttpContext().set(FORCE_REGION, 'west-eu'),
    });
  }

  getAuditTaskErrors(data: any): Observable<any> {
    const email: string = this.localStorageService.get('email') || '';
    const isBehamicsUser = email?.endsWith('@behamics.com') ? true : false;
    const target = this.targetService.getSelectedTarget('target');

    data['target'] = target;
    data['behamicsUser'] = isBehamicsUser;

    let params: string = '';

    Object.keys(data).forEach((key, index) => {
      const value = data[key];
      if (value !== null && value !== undefined) {
        params += `${index === 0 ? '' : '&'}${key}=${value}`;
      }
    });

    return this.http
      .get(`/audit-url/tasks/errors?${params}`, {
        context: new HttpContext().set(FORCE_REGION, 'west-eu'),
      })
      .pipe(map((res) => this.unwrap(res)));
  }

  getAuditTaskShareableToken(data: any): Observable<any> {
    let params: string = '';

    Object.keys(data).forEach((key, index) => {
      const value = data[key];
      if (value !== null && value !== undefined) {
        params += `${index === 0 ? '' : '&'}${key}=${value}`;
      }
    });

    return this.http.post(
      `/audit-url/tasks/enable-share?${params}`,
      {},
      {
        context: new HttpContext().set(FORCE_REGION, 'west-eu'),
      },
    );
  }

  getAuditTaskFromToken(
    token: string,
    competitorUrl?: string,
  ): Observable<any> {
    // Normalize the shared-task response the same way as the single-task
    // endpoint: flatten `target` to the top level and attach the redesigned
    // overview/sections/competitor blocks the tabs consume. normalizeTask is a
    // no-op for the older flat shape, so this stays safe either way.
    let url = `/audit-url/tasks/share?token=${token}`;
    // Scope the shared task to the selected competitor (public equivalent of
    // getAuditTask(taskId, competitorUrl) — the token can't hit that authed
    // endpoint).
    if (competitorUrl) {
      url += `&competitorUrl=${encodeURIComponent(competitorUrl)}`;
    }

    return this.http
      .get(url, {
        context: new HttpContext().set(FORCE_REGION, 'west-eu'),
      })
      .pipe(map((res) => this.normalizeTask(res)));
  }

  // Aggregated crawler visibility results. Two lookup modes:
  //  • Authenticated (Mode A): by the audit taskId — /crawler/experiment?taskId=…
  //  • Shared link (Mode C): the token resolves target + audit server-side.
  getCrawlerExperiment(
    taskId: string,
    token?: string,
    competitorUrl?: string,
  ): Observable<ExperimentResponse> {
    const target = this.targetService.getSelectedTarget('target') || '';

    let params = new HttpParams().set('target', target);
    // Send the taskId whenever we have it, plus the token in share mode. The
    // token authorizes the unauthenticated share request; the taskId is an
    // explicit hint so the token path can resolve the same experiment the
    // authenticated (taskId) path returns.
    if (taskId) {
      params = params.set('taskId', taskId);
    }
    if (token) {
      params = params.set('token', token);
    }

    // Scope the experiment to the selected competitor (mirrors the audit-task
    // endpoint). Ignored server-side for payloads that bundle all competitors.
    if (competitorUrl) {
      params = params.set('competitorUrl', competitorUrl);
    }

    return this.http.get<ExperimentResponse>('/crawler/experiment', {
      params,
      context: new HttpContext().set(FORCE_REGION, 'west-eu'),
    });
  }
}
